import { useEffect } from 'react';
import { useAllServiceBoxSettings, getImageUrl } from '@/hooks/useAllServiceBoxSettings';
import { useHeroImageSettings } from '@/hooks/useHeroImageSettings';

/**
 * Preloader component that loads critical images in the background
 * This helps improve perceived performance by loading images before they're needed
 */
export const ImagePreloader = () => {
  const { data: allSettings } = useAllServiceBoxSettings();
  
  const { getImageUrl: getShowsHeroUrl } = useHeroImageSettings("shows");
  const { getImageUrl: getCoursesHeroUrl } = useHeroImageSettings("courses");
  const { getImageUrl: getAboutHeroUrl } = useHeroImageSettings("about");

  useEffect(() => {
    const imagesToPreload: string[] = [];
    
    if (allSettings) {
      const serviceBoxImages = [
        allSettings.forestallningar.image_file,
        allSettings.kurser.image_file,
        allSettings.underhallning.image_file
      ].filter(Boolean);
      
      serviceBoxImages.forEach((imageFile) => {
        const url = getImageUrl(imageFile);
        if (url) imagesToPreload.push(url);
      });
    }
    
    const heroUrls = [
      getShowsHeroUrl(),
      getCoursesHeroUrl(),
      getAboutHeroUrl()
    ].filter(Boolean) as string[];
    
    imagesToPreload.push(...heroUrls);

    const addedLinks: HTMLLinkElement[] = [];

    imagesToPreload.forEach((url) => {
      if (!url) return;
      // Skip if a preload for this URL already exists
      const existing = document.querySelector(`link[rel="preload"][href="${url}"]`);
      if (existing) return;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
      addedLinks.push(link);
    });

    return () => {
      addedLinks.forEach((link) => {
        try { document.head.removeChild(link); } catch {}
      });
    };
  }, [allSettings, getShowsHeroUrl, getCoursesHeroUrl, getAboutHeroUrl]);

  return null;
};
